import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { BellIcon1Svg } from "../../svgs/bellIcon1Svg";
import { GraduationHatIconSvg } from "../../svgs/graduationHatIconSvg";
import SERVICES from "../../api/services/SERVICES";
import FORMS from "../../api/services/FORMS";
import { toast } from "react-toastify";
import Spinner from "../../components/spinner";
import ColorPicker from "../../components/colorPicker";
import { paths } from "../../routes/paths";
import { useBusinessCapabilities } from "../../hooks/useBusinessCapabilities";
import styles from "../../styles/_containers/_services/create.module.scss";

const Create = ({ businessId, isOpen }) => {
  const navigate = useNavigate();
  const { isPro, loading: capLoading } = useBusinessCapabilities();
  const fileInputRef = useRef(null);

  // ── Active tab ──────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("basic"); // "basic" | "advanced"

  // ── Form data (existing + new) ──────────────────────────────────
  const [formdata, setFormdata] = useState({
    id: "",
    type: "REGULAR",
    name: "",
    description: "",
    time: "",
    price: "",
    maximumQueue: 1,
    employeeId: "",
    isExactPrice: true,
    restingTime: "",
    offsetTime: "",
    offsetNum: "",
    location: "",
    forSubscriptionOnly: false,
    freeTrialEnabled: false,
    showParticipantCount: true,
    requiredFormId: "",
    color: "",
    // ── New fields ──
    isHidden: false,
    businessApproval: false,
    allowSingleEntryPurchase: false,
    singleEntryPrice: "",
    purchasable: false,
    amountToPay: "",
    cancellationFee: "",
  });

  const [loading, setLoading] = useState(false);
  const [forms, setForms] = useState([]);

  // ── Categories ──────────────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const categoryRef = useRef(null);

  // ── Sub-services ────────────────────────────────────────────────
  const [subServices, setSubServices] = useState([]);

  // ── Service image ───────────────────────────────────────────────
  const [serviceImage, setServiceImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // ── Complementary products ──────────────────────────────────────
  const [allProducts, setAllProducts] = useState([]);
  const [complementaryProducts, setComplementaryProducts] = useState([]);
  const [chargeComplementaryProducts, setChargeComplementaryProducts] = useState(false);

  // ── Close category dropdown on outside click ────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setCategoryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Load forms ──────────────────────────────────────────────────
  useEffect(() => {
    const loadForms = async () => {
      try {
        const [decl, quest] = await Promise.all([FORMS.list("declaration"), FORMS.list("questionnaire")]);
        setForms([...(decl?.data || []), ...(quest?.data || [])]);
      } catch (e) { console.error(e); }
    };
    loadForms();
  }, []);

  // ── Load categories ─────────────────────────────────────────────
  const fetchCategories = async () => {
    try {
      const res = await SERVICES.get_categories();
      const cats = res?.categories || res?.data?.categories || [];
      setCategories(cats.map(c => ({ label: c.name, value: c._id })));
    } catch (e) { console.error("Error fetching categories:", e); }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ── Load products ───────────────────────────────────────────────
  const fetchProducts = async () => {
    try {
      const res = await SERVICES.get_all_products();
      const products = res?.data || res?.products || [];
      setAllProducts(Array.isArray(products) ? products : []);
    } catch (e) { console.error("Error fetching products:", e); }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // ── Load service for editing ────────────────────────────────────
  useEffect(() => {
    if (isOpen !== "create") {
      const fetchData = async () => {
        try {
          const res = await SERVICES.get_by_id(isOpen);
          if (res.code === 200) {
            const d = res.data;
            let offsetUnit = d.timeOffset ? d.timeOffset.slice(d.timeOffset.length - 1) : "";
            let offsetNum = d.timeOffset ? d.timeOffset.slice(0, d.timeOffset.length - 1) : "";

            setFormdata({
              type: d.type,
              name: d.name,
              description: d.description,
              time: d.time,
              price: d.price,
              maximumQueue: d.maximumQueue,
              isExactPrice: d.isExactPrice,
              restingTime: d.restingTime,
              offsetTime: offsetUnit,
              offsetNum: offsetNum,
              location: d.location || "",
              forSubscriptionOnly: d.forSubscriptionOnly,
              freeTrialEnabled: d.freeTrialEnabled || false,
              showParticipantCount: d.showParticipantCount !== false,
              id: d._id,
              requiredFormId: d.requiredFormId || "",
              color: d.color || "",
              // New fields
              isHidden: d.isHidden || false,
              businessApproval: d.businessApproval || false,
              allowSingleEntryPurchase: d.allowSingleEntryPurchase || false,
              singleEntryPrice: d.singleEntryPrice || "",
              purchasable: d.purchasable || false,
              amountToPay: d.amountToPay || "",
              cancellationFee: d.cancellationFee || "",
            });

            // Categories
            if (d.categories?.length > 0) {
              setSelectedCategories(d.categories.map(c => c._id || c.id || c).filter(Boolean).map(String));
            } else if (d.category) {
              const catId = d.category._id || d.category.id || d.category;
              if (catId) setSelectedCategories([String(catId)]);
            }

            // Sub-services
            if (d.subServices?.length > 0) {
              setSubServices(d.subServices);
            }

            // Complementary products
            if (d.complementaryProducts?.length > 0) {
              setComplementaryProducts(d.complementaryProducts.map(p => p._id || p));
            }
            if (d.chargeComplementaryProducts) {
              setChargeComplementaryProducts(true);
            }

            // Image preview
            if (d.image) {
              setImagePreview(d.image);
            }
          }
        } catch (error) {
          console.log(error);
        }
      };
      fetchData();
    } else {
      // Reset all state for create
      setFormdata({
        id: "",
        type: "REGULAR",
        name: "",
        description: "",
        time: "",
        price: "",
        maximumQueue: 1,
        employeeId: "",
        isExactPrice: true,
        restingTime: "",
        offsetTime: "",
        offsetNum: "",
        location: "",
        forSubscriptionOnly: false,
        freeTrialEnabled: false,
        showParticipantCount: true,
        requiredFormId: "",
        color: "",
        isHidden: false,
        businessApproval: false,
        allowSingleEntryPurchase: false,
        singleEntryPrice: "",
        purchasable: false,
        amountToPay: "",
        cancellationFee: "",
      });
      setSelectedCategories([]);
      setSubServices([]);
      setServiceImage(null);
      setImagePreview(null);
      setComplementaryProducts([]);
      setChargeComplementaryProducts(false);
      setActiveTab("basic");
    }
  }, [isOpen]);

  const handleChange = (e) => {
    setFormdata((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // ── Build payload ───────────────────────────────────────────────
  const buildPayload = () => ({
    employeeId: businessId,
    type: formdata.type,
    name: formdata.name,
    description: formdata.description,
    time: formdata.time,
    price: formdata.price,
    maximumQueue: formdata.maximumQueue,
    isExactPrice: formdata.isExactPrice,
    restingTime: formdata.restingTime,
    timeOffset: formdata.offsetNum + formdata.offsetTime,
    location: formdata.location,
    forSubscriptionOnly: formdata.forSubscriptionOnly,
    freeTrialEnabled: formdata.freeTrialEnabled,
    showParticipantCount: formdata.showParticipantCount,
    requiredFormId: formdata.requiredFormId || null,
    color: formdata.color,
    // New fields
    isHidden: formdata.isHidden,
    businessApproval: formdata.businessApproval,
    allowSingleEntryPurchase: formdata.forSubscriptionOnly ? formdata.allowSingleEntryPurchase : false,
    singleEntryPrice: formdata.allowSingleEntryPurchase && formdata.forSubscriptionOnly ? Number(formdata.singleEntryPrice) || 0 : 0,
    purchasable: formdata.purchasable,
    amountToPay: formdata.amountToPay,
    cancellationFee: formdata.cancellationFee,
    selectedCategory: selectedCategories[0] || "",
    selectedCategories: selectedCategories,
    subServices: subServices,
    complementaryProducts: complementaryProducts,
    chargeComplementaryProducts: chargeComplementaryProducts,
  });

  // ── Create service ──────────────────────────────────────────────
  const handleCreateService = async () => {
    setLoading(true);
    try {
      const res = await SERVICES.create(buildPayload());
      toast.success(res.data.message);
      navigate(paths.services);
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || "Error creating service");
    }
    setLoading(false);
  };

  // ── Edit service ────────────────────────────────────────────────
  const handleEditService = async () => {
    setLoading(true);
    try {
      const payload = { ...buildPayload(), id: formdata.id };
      const res = await SERVICES.update(payload);
      toast.success(res.data.message);
      navigate(paths.services);
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || "Error updating service");
    }
    setLoading(false);
  };

  // ── Category helpers ────────────────────────────────────────────
  const toggleCategory = (catId) => {
    setSelectedCategories(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    if (categories.some(c => c.label === newCategoryName.trim())) {
      toast.error("Category already exists");
      return;
    }
    try {
      await SERVICES.create_category({ name: newCategoryName.trim() });
      toast.success("Category created");
      setNewCategoryName("");
      await fetchCategories();
    } catch (e) {
      console.error(e);
      toast.error("Error creating category");
    }
  };

  // ── Sub-service helpers ─────────────────────────────────────────
  const addSubService = () => {
    setSubServices(prev => [...prev, { name: "", description: "", time: "", price: "", purchasable: false }]);
  };

  const updateSubService = (index, field, value) => {
    setSubServices(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeSubService = (index) => {
    setSubServices(prev => prev.filter((_, i) => i !== index));
  };

  // ── Image helpers ───────────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setServiceImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // ── Pro lock wrapper ────────────────────────────────────────────
  const ProWrap = ({ children, label }) => {
    if (isPro) return children;
    return (
      <div className={styles.proLocked} title="Available with Pro plan">
        {children}
        <span className={styles.proBadge}>PRO</span>
      </div>
    );
  };

  // ── Toggle component (pure CSS, no react-switch) ────────────────
  const ToggleSwitch = ({ checked, onChange, label, indent = false }) => (
    <div className={styles.toggleRow} style={indent ? { paddingInlineStart: 24 } : {}}>
      <button
        type="button"
        className={`${styles.toggle} ${checked ? styles.toggleOn : ""}`}
        onClick={() => onChange(!checked)}
      >
        <span className={styles.toggleThumb} />
      </button>
      <span className={styles.toggleLabel}>{label}</span>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════
  return (
    <div className={styles.container}>
      <div className={styles.wrapper} data-tour="service-form">
        <button className={styles.backBtn} onClick={() => navigate(paths.services)}>
          &larr; חזרה לשירותים
        </button>
        <h2>{isOpen === "create" ? "צור שירות חדש עבור הלקוחות שלך" : "עריכת שירות"}</h2>
        <p className={styles.mainText}>ניתן לערוך או למחוק בכל עת לבחירתכם!</p>

        {/* ── Tabs ────────────────────────────────────────── */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === "basic" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("basic")}
          >
            בסיסי
          </button>
          <button
            className={`${styles.tab} ${activeTab === "advanced" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("advanced")}
          >
            הגדרות מתקדמות
          </button>
        </div>

        <div className={styles.form}>
          {/* ═══════════════════════════════════════════════ */}
          {/* TAB 1: BASIC                                   */}
          {/* ═══════════════════════════════════════════════ */}
          {activeTab === "basic" && (
            <>
              {/* Service type toggle */}
              <div className={`${styles.types} ${styles.fullWidth}`}>
                <h4>סוג השירות</h4>
                <div className={styles.row}>
                  <div
                    className={`${styles.type} ${formdata.type === "REGULAR" ? styles.active : ""}`}
                    onClick={() => setFormdata(prev => ({ ...prev, type: "REGULAR" }))}
                  >
                    <BellIcon1Svg fill={formdata.type === "REGULAR" ? "#DE1468" : "#CFCFCF"} />
                    <h5>שירות רגיל</h5>
                    <button>נבחר</button>
                  </div>
                  <div
                    className={`${styles.type} ${formdata.type === "CLASS" ? styles.active : ""}`}
                    onClick={() => setFormdata(prev => ({ ...prev, type: "CLASS" }))}
                  >
                    <GraduationHatIconSvg fill={formdata.type === "CLASS" ? "#DE1468" : "#CFCFCF"} />
                    <h5>שיעור</h5>
                    <button>בחירה</button>
                  </div>
                </div>
              </div>

              {/* 2-column grid */}
              <div className={styles.grid}>
                {/* ─── LEFT COLUMN ─── */}
                {/* Service Name */}
                <div className={styles.field}>
                  <label>שם השירות <span>*</span></label>
                  <input
                    type="text"
                    name="name"
                    placeholder="רשום את שם השירות"
                    value={formdata.name}
                    onChange={handleChange}
                    data-tour="service-name-input"
                  />
                </div>

                {/* ─── RIGHT COLUMN ─── */}
                {/* Description */}
                <div className={styles.field}>
                  <label>תיאור השירות <span>(אופציונלי)</span></label>
                  <input
                    type="text"
                    name="description"
                    value={formdata.description}
                    onChange={handleChange}
                    placeholder="לדוגמא: ניקוי עמוק, נא להגיע עם חלוק"
                  />
                </div>

                {/* Service Time */}
                <div className={styles.field}>
                  <label>זמן השירות (דקות) <span>*</span></label>
                  <input
                    type="number"
                    name="time"
                    placeholder="35"
                    value={formdata.time}
                    onChange={handleChange}
                  />
                </div>

                {/* Location */}
                <div className={styles.field}>
                  <label>מיקום <span>(אופציונלי)</span></label>
                  <input
                    type="text"
                    name="location"
                    value={formdata.location}
                    onChange={handleChange}
                    placeholder="הזן את מיקום השירות"
                  />
                </div>

                {/* Price + price type */}
                <div className={styles.field}>
                  <label>מחיר השירות <span>(אופציונלי)</span></label>
                  <div className={styles.fixedPrice}>
                    <select name="isExactPrice" value={formdata.isExactPrice} onChange={handleChange}>
                      <option value={true}>מחיר קבוע</option>
                      <option value={false}>החל מ</option>
                    </select>
                    <input
                      type="number"
                      name="price"
                      placeholder="₪150"
                      value={formdata.price}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Rest Time */}
                <div className={styles.field}>
                  <label>זמן מנוחה/ הפסקה <span>(אופציונלי)</span></label>
                  <input
                    type="number"
                    name="restingTime"
                    value={formdata.restingTime}
                    onChange={handleChange}
                    placeholder="דקות הפסקה לאחר השירות"
                  />
                </div>

                {/* Maximum Queue */}
                <div className={styles.numberOfCustomers}>
                  <h4>כמות לקוחות בו-זמנית <span>*</span></h4>
                  <div className={styles.counter}>
                    <span onClick={() => setFormdata(prev => ({ ...prev, maximumQueue: prev.maximumQueue + 1 }))}>+</span>
                    <input type="text" readOnly value={formdata.maximumQueue} />
                    <span onClick={() => setFormdata(prev => ({ ...prev, maximumQueue: prev.maximumQueue > 1 ? prev.maximumQueue - 1 : 1 }))}>-</span>
                  </div>
                </div>

                {/* Future Booking Offset */}
                <div className={styles.field}>
                  <label>זמן קביעת תור עתידי</label>
                  <div className={styles.fixedPrice}>
                    <select name="offsetTime" value={formdata.offsetTime} onChange={handleChange}>
                      <option value="h">שעות</option>
                      <option value="d">ימים</option>
                      <option value="w">שבועות</option>
                      <option value="m">חודשים</option>
                      <option value="y">שנים</option>
                    </select>
                    <input
                      type="number"
                      name="offsetNum"
                      placeholder="1"
                      value={formdata.offsetNum}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Color Picker */}
                <div className={styles.field}>
                  <ColorPicker
                    value={formdata.color}
                    onChange={(c) => setFormdata(prev => ({ ...prev, color: c }))}
                  />
                </div>

                {/* Required Form */}
                <div className={styles.field}>
                  <label>טופס/שאלון נדרש לפני תור ראשון</label>
                  {forms.filter(f => f.isActive).length === 0 ? (
                    <p style={{ fontSize: 13, color: "#94a3b8", margin: "6px 0" }}>
                      עדיין לא הוקמו טפסים/שאלונים.{" "}
                      <a href="/forms" style={{ color: "#DE1468", fontWeight: 600, textDecoration: "none" }}>
                        להקמה לחצו כאן
                      </a>
                    </p>
                  ) : (
                    <select
                      name="requiredFormId"
                      value={formdata.requiredFormId || ""}
                      onChange={handleChange}
                      className={styles.select}
                    >
                      <option value="">ללא טופס</option>
                      {forms.filter(f => f.isActive).map(f => (
                        <option key={f._id} value={f._id}>
                          {f.type === "declaration" ? "📋" : "📝"} {f.title}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Show Participant Count — full width */}
                <div className={styles.fullWidth}>
                  <ToggleSwitch
                    checked={formdata.showParticipantCount}
                    onChange={(e) => setFormdata(prev => ({ ...prev, showParticipantCount: e }))}
                    label="הצג מספר משתתפים ללקוחות"
                  />
                </div>
              </div>
            </>
          )}

          {/* ═══════════════════════════════════════════════ */}
          {/* TAB 2: ADVANCED SETTINGS                       */}
          {/* ═══════════════════════════════════════════════ */}
          {activeTab === "advanced" && (
            <div className={styles.grid}>
              {/* ─── LEFT COLUMN ─── */}

              {/* Categories */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionTitle}>קטגוריות</div>
                <div className={styles.categorySelect} ref={categoryRef}>
                  <div
                    className={styles.categoryTrigger}
                    onClick={() => setCategoryDropdownOpen(prev => !prev)}
                  >
                    {selectedCategories.length > 0 ? (
                      <div className={styles.categoryTags}>
                        {selectedCategories.map(catId => {
                          const cat = categories.find(c => c.value === catId);
                          return (
                            <span key={catId} className={styles.categoryTag}>
                              {cat?.label || catId}
                              <span
                                className={styles.removeTag}
                                onClick={(e) => { e.stopPropagation(); toggleCategory(catId); }}
                              >
                                x
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span>בחרו קטגוריות...</span>
                    )}
                  </div>

                  {categoryDropdownOpen && (
                    <div className={styles.categoryDropdown}>
                      {categories.length === 0 ? (
                        <p style={{ padding: "12px", textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
                          אין קטגוריות עדיין
                        </p>
                      ) : (
                        categories.map(cat => (
                          <div
                            key={cat.value}
                            className={`${styles.categoryItem} ${selectedCategories.includes(cat.value) ? styles.categoryItemActive : ""}`}
                            onClick={() => toggleCategory(cat.value)}
                          >
                            <span>{cat.label}</span>
                            {selectedCategories.includes(cat.value) && <span>✓</span>}
                          </div>
                        ))
                      )}
                      <div className={styles.addCategoryRow}>
                        <input
                          type="text"
                          placeholder="שם קטגוריה חדשה..."
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => { if (e.key === "Enter") handleAddCategory(); }}
                        />
                        <button onClick={handleAddCategory}>הוסף</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ─── RIGHT COLUMN ─── */}

              {/* Subscription & related toggles */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionTitle}>מנויים ותשלומים</div>

                <ToggleSwitch
                  checked={formdata.forSubscriptionOnly}
                  onChange={(e) => setFormdata(prev => ({
                    ...prev,
                    forSubscriptionOnly: e,
                    freeTrialEnabled: e ? prev.freeTrialEnabled : false,
                    allowSingleEntryPurchase: e ? prev.allowSingleEntryPurchase : false,
                  }))}
                  label="כניסה למנוים בלבד"
                />

                {formdata.forSubscriptionOnly && (
                  <>
                    <ToggleSwitch
                      checked={formdata.freeTrialEnabled}
                      onChange={(e) => setFormdata(prev => ({ ...prev, freeTrialEnabled: e }))}
                      label="שיעור ניסיון ראשון בחינם (ללא צורך במנוי)"
                      indent
                    />

                    <ProWrap>
                      <ToggleSwitch
                        checked={formdata.allowSingleEntryPurchase}
                        onChange={(e) => setFormdata(prev => ({ ...prev, allowSingleEntryPurchase: e }))}
                        label="אפשר רכישת כניסה בודדת"
                        indent
                      />
                    </ProWrap>

                    {formdata.allowSingleEntryPurchase && isPro && (
                      <div className={styles.field} style={{ paddingInlineStart: 24 }}>
                        <label>מחיר כניסה בודדת (₪)</label>
                        <input
                          type="number"
                          name="singleEntryPrice"
                          placeholder="0"
                          value={formdata.singleEntryPrice}
                          onChange={handleChange}
                        />
                      </div>
                    )}
                  </>
                )}

                <ProWrap>
                  <ToggleSwitch
                    checked={formdata.purchasable}
                    onChange={(e) => setFormdata(prev => ({ ...prev, purchasable: e }))}
                    label="שירות לרכישה מקוונת"
                  />
                </ProWrap>

                {formdata.purchasable && isPro && (
                  <div className={styles.field} style={{ paddingInlineStart: 24 }}>
                    <label>סכום לתשלום (₪)</label>
                    <input
                      type="number"
                      name="amountToPay"
                      placeholder="0"
                      value={formdata.amountToPay}
                      onChange={handleChange}
                    />
                  </div>
                )}

                <ProWrap>
                  <div className={styles.field}>
                    <label>עמלת ביטול (₪)</label>
                    <input
                      type="number"
                      name="cancellationFee"
                      placeholder="0"
                      value={formdata.cancellationFee}
                      onChange={handleChange}
                    />
                  </div>
                </ProWrap>
              </div>

              {/* Sub-Services — full width */}
              <div className={`${styles.sectionCard} ${styles.fullWidth}`}>
                <div className={styles.sectionTitle}>תת-שירותים</div>

                {subServices.map((sub, idx) => (
                  <div key={idx} className={styles.subServiceItem}>
                    <div className={styles.subServiceHeader}>
                      <span>תת-שירות {idx + 1}</span>
                      <button className={styles.removeBtn} onClick={() => removeSubService(idx)}>
                        הסרה
                      </button>
                    </div>
                    <div className={styles.subServiceGrid}>
                      <div className={styles.field}>
                        <label>שם</label>
                        <input
                          type="text"
                          value={sub.name}
                          onChange={(e) => updateSubService(idx, "name", e.target.value)}
                          placeholder="שם תת-שירות"
                        />
                      </div>
                      <div className={styles.field}>
                        <label>תיאור</label>
                        <input
                          type="text"
                          value={sub.description}
                          onChange={(e) => updateSubService(idx, "description", e.target.value)}
                          placeholder="תיאור"
                        />
                      </div>
                      <div className={styles.field}>
                        <label>זמן (דקות)</label>
                        <input
                          type="number"
                          value={sub.time}
                          onChange={(e) => updateSubService(idx, "time", e.target.value)}
                          placeholder="30"
                        />
                      </div>
                      <div className={styles.field}>
                        <label>מחיר (₪)</label>
                        <input
                          type="number"
                          value={sub.price}
                          onChange={(e) => updateSubService(idx, "price", e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button className={styles.addBtn} onClick={addSubService}>
                  + הוסף תת-שירות
                </button>
              </div>

              {/* Service Image */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionTitle}>תמונת שירות</div>
                <div className={styles.imageUpload}>
                  {imagePreview && (
                    <img src={imagePreview} alt="service" className={styles.imagePreview} />
                  )}
                  <div
                    className={styles.uploadArea}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {imagePreview ? "החלף תמונה" : "לחצו להעלאת תמונה"}
                  </div>
                </div>
              </div>

              {/* Toggles Column */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionTitle}>הגדרות נוספות</div>

                <ToggleSwitch
                  checked={formdata.isHidden}
                  onChange={(e) => setFormdata(prev => ({ ...prev, isHidden: e }))}
                  label="הסתר שירות מלקוחות"
                />

                <ToggleSwitch
                  checked={formdata.businessApproval}
                  onChange={(e) => setFormdata(prev => ({ ...prev, businessApproval: e }))}
                  label="אישור ידני נדרש לכל תור"
                />
              </div>

              {/* Complementary Products — full width */}
              <ProWrap>
                <div className={`${styles.sectionCard} ${styles.fullWidth}`}>
                  <div className={styles.sectionTitle}>מוצרים משלימים</div>
                  <div className={styles.productSelect}>
                    {allProducts.length === 0 ? (
                      <p className={styles.emptyProducts}>אין מוצרים זמינים</p>
                    ) : (
                      <div className={styles.productList}>
                        {allProducts.map(product => (
                          <label key={product._id} className={styles.productItem}>
                            <input
                              type="checkbox"
                              checked={complementaryProducts.includes(product._id)}
                              onChange={() => {
                                setComplementaryProducts(prev =>
                                  prev.includes(product._id)
                                    ? prev.filter(id => id !== product._id)
                                    : [...prev, product._id]
                                );
                              }}
                            />
                            <span>{product.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    {complementaryProducts.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <ToggleSwitch
                          checked={chargeComplementaryProducts}
                          onChange={(e) => setChargeComplementaryProducts(e)}
                          label="חיוב מוצרים משלימים"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </ProWrap>
            </div>
          )}

          {/* ── Submit Button ─────────────────────────────── */}
          <div style={{ marginTop: 24 }}>
            {isOpen === "create" ? (
              <button
                className={styles.submitBtn}
                onClick={handleCreateService}
                data-tour="service-save-button"
              >
                {loading ? <Spinner /> : "צור שירות חדש"}
              </button>
            ) : (
              <button
                className={styles.submitBtn}
                onClick={handleEditService}
                data-tour="service-save-button"
              >
                {loading ? <Spinner /> : "שמירת נתונים"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Create;
